import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Collapse, Popover, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  CaretDown,
  CaretRight,
  DotsThreeOutline,
  Folder as FolderIcon,
  FolderUser,
  PencilSimpleLine,
  Plus,
  Trash,
} from '@phosphor-icons/react';
import { Folder, Flows } from '../../../../util/Types/Folder';
import { FolderService } from '../../../../service/FolderService';
import { isVirtualFolderId } from '../utils';
import { useAgentsSidebarContext } from './context';
import { AgentItem } from './AgentItem';

const FolderHeader = styled(Box)(({ theme }) => {
  const palette = theme.customColorPalette;
  const isDarkMode = theme.palette.mode === 'dark';
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '4px 8px',
    cursor: 'pointer',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: isDarkMode ? palette.gray[800] : palette.blue[50],
    },
  };
});

interface FolderItemProps {
  folder: Folder;
  customItems?: React.ReactNode[];
  onFlowRename?: (flow: Flows, folderId: string) => void;
  onFlowDelete?: (flow: Flows) => void;
  onFlowShare?: (flow: Flows, folderId: string) => void;
  onFlowOpenInNewTab?: (flow: Flows) => void;
  onFlowCreateInterface?: (flow: Flows, folderId: string) => void;
  onFlowAddToLibrary?: (flow: Flows, folderId: string) => void;
  onFlowDuplicate?: (flow: Flows, folderId: string) => void;
  onFolderRename?: (folder: Folder) => void;
  onFolderDelete?: (folder: Folder) => void;
  onAddAgent?: (folder: Folder) => void;
}

export const FolderItem: React.FC<FolderItemProps> = ({
  folder,
  customItems,
  onFlowRename,
  onFlowDelete,
  onFlowShare,
  onFlowOpenInNewTab,
  onFlowCreateInterface,
  onFlowAddToLibrary,
  onFlowDuplicate,
  onFolderRename,
  onFolderDelete,
  onAddAgent,
}) => {
  const { isDarkMode, palette } = useAgentsSidebarContext();
  const [isOpen, setIsOpen] = useState(false);
  const [localFolder, setLocalFolder] = useState(folder);
  const [isHovered, setIsHovered] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const folderHeaderRef = useRef<HTMLDivElement>(null);

  const hasFolderActions =
    !isVirtualFolderId(folder.id) && (onFolderRename || onFolderDelete);
  const showAddAgent = !isVirtualFolderId(folder.id) && !!onAddAgent;

  useEffect(() => {
    setLocalFolder(prev => ({
      ...prev,
      ...folder,
      folders: folder.folders?.length ? folder.folders : prev.folders,
      flows: folder.flows?.length ? folder.flows : prev.flows,
    }));
  }, [folder]);

  const textColor = isDarkMode ? palette.gray[300] : palette.gray[600];

  const hasItems = () => {
    return (
      (customItems?.length ?? 0) > 0 ||
      (localFolder.flows?.length ?? 0) > 0 ||
      (localFolder.folders?.length ?? 0) > 0
    );
  };

  const handleToggle = async () => {
    if (!isOpen && (!localFolder.flows || localFolder.flows.length === 0)) {
      if (!isVirtualFolderId(folder.id)) {
        try {
          const res = await FolderService.getOne(folder.id);
          setLocalFolder(prev => ({
            ...prev,
            ...res.data,
            folders: prev.folders?.length ? prev.folders : res.data.folders,
          }));
        } catch (error) {
          console.error('Failed to load folder:', error);
        }
      }
    }
    setIsOpen(!isOpen);
  };

  const handleMenuOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    const header = folderHeaderRef.current;
    if (header) {
      setMenuAnchor(header);
    }
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  return (
    <Box>
      <FolderHeader
        ref={folderHeaderRef}
        onClick={handleToggle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flex: 1,
            minWidth: 0,
          }}
        >
          {folder.name?.toLowerCase() === 'shared' ? (
            <FolderUser size={16} color={textColor} />
          ) : (
            <FolderIcon size={16} color={textColor} />
          )}
          <Typography
            sx={{
              color: textColor,
              fontFamily: 'Roboto',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '20px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
              flex: 1,
            }}
          >
            {folder.name}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '2px',
            color: textColor,
            flexShrink: 0,
          }}
        >
          {(isHovered || menuAnchor) && showAddAgent && (
            <Box
              component="button"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onAddAgent?.(folder);
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '2px',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                color: isDarkMode ? palette.gray[400] : palette.gray[500],
                '&:hover': {
                  backgroundColor: isDarkMode ? palette.gray[800] : palette.gray[200],
                },
              }}
            >
              <Plus size={16} color={isDarkMode ? palette.gray[400] : palette.gray[500]} />
            </Box>
          )}
          {(isHovered || menuAnchor) && hasFolderActions && (
            <Box
              component="button"
              onClick={handleMenuOpen}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '2px',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                color: isDarkMode ? palette.gray[400] : palette.gray[500],
                '&:hover': {
                  backgroundColor: isDarkMode ? palette.gray[800] : palette.gray[200],
                },
              }}
            >
              <DotsThreeOutline size={16} weight="fill" color={isDarkMode ? palette.gray[400] : palette.gray[500]} />
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', color: textColor }}>
          {isOpen ? <CaretDown size={16} /> : <CaretRight size={16} />}
        </Box>
      </FolderHeader>
      <Popover
        open={Boolean(menuAnchor)}
        anchorEl={menuAnchor}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: { marginLeft: "8px" },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            p: "8px",
            background: isDarkMode ? palette.gray[900] : "#FFFFFF",
            minWidth: "180px",
          }}
        >
          {onFolderRename && (
            <Button
              variant="contained"
              onClick={() => {
                handleMenuClose();
                onFolderRename(folder);
              }}
              sx={{
                width: "100%",
                background: "unset",
                justifyContent: "flex-start",
                textTransform: "unset",
                fontSize: "16px",
                boxShadow: "unset",
                gap: "16px",
                px: "8px",
                color: isDarkMode ? palette.gray[200] : palette.gray[700],
                "&:hover": {
                  background: isDarkMode ? palette.gray[700] : palette.gray[100],
                  boxShadow: "unset",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <PencilSimpleLine
                  size={20}
                  color={isDarkMode ? palette.gray[400] : palette.gray[500]}
                />
                <Typography
                  sx={{
                    color: isDarkMode ? palette.gray[200] : palette.gray[700],
                  }}
                >
                  Update Folder
                </Typography>
              </Box>
            </Button>
          )}
          {onFolderDelete && (
            <Button
              variant="contained"
              onClick={() => {
                handleMenuClose();
                onFolderDelete(folder);
              }}
              sx={{
                width: "100%",
                background: "unset",
                justifyContent: "flex-start",
                gap: "16px",
                textTransform: "unset",
                fontSize: "16px",
                boxShadow: "unset",
                color: "#b91c1c",
                px: "8px",
                "&:hover": {
                  background: "#fecdd3",
                  boxShadow: "unset",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <Trash size={20} color="#b91c1c" />
                <Typography>Delete</Typography>
              </Box>
            </Button>
          )}
        </Box>
      </Popover>
      <Collapse in={isOpen}>
        <Box sx={{ marginLeft: '8px' }}>
          {hasItems() ? (
            customItems ? (
              customItems
            ) : (
              <>
                {localFolder.folders?.map(subfolder => (
                  <FolderItem
                    key={subfolder.id}
                    folder={subfolder}
                    onFlowRename={onFlowRename}
                    onFlowDelete={onFlowDelete}
                    onFlowShare={onFlowShare}
                    onFlowOpenInNewTab={onFlowOpenInNewTab}
                    onFlowCreateInterface={onFlowCreateInterface}
                    onFlowAddToLibrary={onFlowAddToLibrary}
                    onFlowDuplicate={onFlowDuplicate}
                    onFolderRename={onFolderRename}
                    onFolderDelete={onFolderDelete}
                    onAddAgent={onAddAgent}
                  />
                ))}
                {localFolder.flows?.map(flow => (
                  <AgentItem
                    key={flow.id}
                    flow={flow}
                    folderId={folder.id}
                    flowFolderName={localFolder.name}
                    onRename={onFlowRename}
                    onDelete={onFlowDelete}
                    onShare={onFlowShare}
                    onOpenInNewTab={onFlowOpenInNewTab}
                    onCreateInterface={onFlowCreateInterface}
                    onAddToLibrary={onFlowAddToLibrary}
                    onDuplicate={onFlowDuplicate}
                  />
                ))}
              </>
            )
          ) : (
            <Typography
              sx={{
                color: isDarkMode ? palette.gray[500] : palette.gray[400],
                fontFamily: 'Roboto',
                fontSize: '12px',
                fontStyle: 'italic',
                padding: '8px 16px',
              }}
            >
              No items
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};
